import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import GoalForm from "@pages/Program/components/goal/GoalForm";
import { GoalPayload } from "@shared/interfaces/ProgramFiles";
import { toastService } from "@services/ToastService";
import useHandleSaveGoal from "@pages/Program/components/goal/utils/useHandleSaveGoal";
import { useParams } from "react-router-dom";
import Loader from "@shared/components/Loader";

interface AddGoalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchGoals: () => Promise<void>;
  startDate: string;
  endDate: string;
}

const AddGoal: React.FC<AddGoalProps> = ({
  isOpen,
  onClose,
  fetchGoals,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(false);
  const { programId } = useParams();

  const methods = useForm<GoalPayload>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      project_ids: [],
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const handleSaveGoal = useHandleSaveGoal({}, methods);

  const onSubmit = async (values: GoalPayload) => {
    if (!isValid) return;
    setLoading(true);

    try {
      if (!programId) return;

      const payload: GoalPayload = {
        ...values,
        reporting_frequency: null,
        program_id: programId,
      };
      await handleSaveGoal(payload);
      fetchGoals();
      onClose();
    } catch (error) {
      console.error("Failed to save goal", error);
      toastService.error("Failed to save goal");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-2xl py-6 shadow-lg">
        <h2 className="text-xl font-semibold pb-4 mb-4 border-b border-[#e5e7eb] px-6">
          Add goal
        </h2>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="px-6">
            <GoalForm startDate={startDate} endDate={endDate} />
            <div className="flex justify-end mt-6 gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border text-sm 2xl:text-base rounded-md text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={!isValid || loading}
                className="px-4 py-2 bg-blue-600 text-sm 2xl:text-base text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader color="#fff" /> : <span>Add Goal</span>}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddGoal;
